import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import {
  Badge,
  Button,
  Col,
  Input,
  Row,
  Segmented,
  Spin,
  notification,
} from "antd";
import Icon from "@ant-design/icons";

import Header from "../../components/layout/header";
import Styles from "../operator/op.module.scss";
import { CustomSpin, FilterIcon, SearchIcon } from "../../assets/icons/icons";
import ListingCards from "../../components/reusable/listingCards";
import UniversalScanButton from "../../components/mobile/UniversalScanButton";
import FilterModal from "../../components/mobile/FilterModal";
import { api } from "../../services/api";
import {
  useAllTotes,
  useAllTrolleys,
  useTotePriorities,
  useToteStatus,
  useTrolleyStatus,
} from "../common/queries";
import type { FilterValues } from "../../components/mobile/types";
import { hasActiveFilters } from "../../utils/filterUtils";
import CONSTANTS from "../../constants/constants";

interface ListingItem {
  toteNo: string | number;
  status: string;
  totalItems: number;
  priority: boolean;
  registeredDate?: string;
  id?: string;
  fullData?: unknown;
}

const SEGMENTS = {
  TOTE: "Tote",
  TROLLEY: "Trolley",
} as const;

type SegmentType = (typeof SEGMENTS)[keyof typeof SEGMENTS];

// Note: These must match EXACTLY with the status names from getTrolleyStatus API
const DEFAULT_TROLLEY_STATUS_NAMES = [
  "Empty - Not Available",
  "Unserviceable - Awaiting Repair",
] as const;

const TOTE_STATUS_KEYWORDS = new Set([
  "available",
  "loaded to trolley",
  "unloaded from trolley",
  "completed",
]);

const SUPERVISOR_TAB_STORAGE_KEY = "supervisorActiveTab";
const SUPERVISOR_TOTE_FILTERS_KEY = "supervisorToteFilters";
const SUPERVISOR_TROLLEY_FILTERS_KEY = "supervisorTrolleyFilters";
const SUPERVISOR_SEARCH_QUERY_KEY = "supervisorSearchQuery";

// Helper function to load filters from sessionStorage
const loadFiltersFromStorage = (key: string): FilterValues | null => {
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error(`Error loading filters from ${key}:`, error);
  }
  return null;
};

// Helper function to save filters to sessionStorage
const saveFiltersToStorage = (key: string, filters: FilterValues | null) => {
  try {
    if (filters && hasActiveFilters(filters)) {
      sessionStorage.setItem(key, JSON.stringify(filters));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error saving filters to ${key}:`, error);
  }
};

const SupervisorMobileLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem(SUPERVISOR_SEARCH_QUERY_KEY) || "";
  });
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [segment, setSegment] = useState<SegmentType>(() => {
    const savedTab = sessionStorage.getItem(SUPERVISOR_TAB_STORAGE_KEY);
    return (savedTab as SegmentType) || SEGMENTS.TOTE;
  });
  const [toteFilters, setToteFilters] = useState<FilterValues | null>(() => {
    return loadFiltersFromStorage(SUPERVISOR_TOTE_FILTERS_KEY);
  });
  const [trolleyFilters, setTrolleyFilters] = useState<FilterValues | null>(
    () => {
      return loadFiltersFromStorage(SUPERVISOR_TROLLEY_FILTERS_KEY);
    }
  );
  const [defaultTrolleyStatusIds, setDefaultTrolleyStatusIds] = useState<number[]>([]);
  const [defaultTrolleyStatusNames, setDefaultTrolleyStatusNames] = useState<string[]>([]);
  const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);
  const [isCheckingTote, setIsCheckingTote] = useState(false);
  const [isCheckingTrolley, setIsCheckingTrolley] = useState(false);

  const { data: toteStatusData } = useToteStatus();
  const { data: priorityOptions } = useTotePriorities();
  const { data: trolleyStatusData } = useTrolleyStatus();

  useEffect(() => {
    const desiredSegment = (
      location.state as { defaultSegment?: SegmentType } | undefined
    )?.defaultSegment;
    if (desiredSegment) {
      setSegment(desiredSegment);
      sessionStorage.setItem(SUPERVISOR_TAB_STORAGE_KEY, desiredSegment);
    }
  }, [location.state]);

  useEffect(() => {
    sessionStorage.setItem(SUPERVISOR_TAB_STORAGE_KEY, segment);
  }, [segment]);

  // Create debounced function using lodash - only create once using useMemo
  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchQuery(value);
      }, 1000),
    []
  );

  // Set initial debounced value on mount only
  useEffect(() => {
    setDebouncedSearchQuery(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update debounced search when searchQuery changes
  useEffect(() => {
    debouncedUpdateSearch(searchQuery);
    
    // Cleanup: cancel pending debounced calls
    return () => {
      debouncedUpdateSearch.cancel();
    };
  }, [searchQuery, debouncedUpdateSearch]);

  // Persist search query to sessionStorage
  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem(SUPERVISOR_SEARCH_QUERY_KEY, searchQuery);
    } else {
      sessionStorage.removeItem(SUPERVISOR_SEARCH_QUERY_KEY);
    }
  }, [searchQuery]);


  const statusMap = useMemo(() => {
    const map = new Map<string, number>();
    toteStatusData?.tote_list?.forEach((item) => {
      map.set(item.status_name.toLowerCase(), item.id);
    });
    return map;
  }, [toteStatusData?.tote_list]);

  const priorityMap = useMemo(() => {
    const map = new Map<string, number>();
    priorityOptions?.forEach((option) => {
      map.set(option.priority.toLowerCase(), option.id);
    });
    return map;
  }, [priorityOptions]);

  useEffect(() => {
    if (trolleyStatusData?.trolley_list) {
      // Try exact match first
      let filtered = trolleyStatusData.trolley_list.filter((item) =>
        DEFAULT_TROLLEY_STATUS_NAMES.includes(
          item.status_name as (typeof DEFAULT_TROLLEY_STATUS_NAMES)[number]
        )
      );
      
      // If no exact matches, try case-insensitive and trimmed matching
      if (filtered.length === 0) {
        const defaultNamesLower = DEFAULT_TROLLEY_STATUS_NAMES.map(name => 
          name.toLowerCase().trim()
        );
        filtered = trolleyStatusData.trolley_list.filter((item) =>
          defaultNamesLower.includes(item.status_name.toLowerCase().trim())
        );
      }
      
      const ids = filtered.map((item) => item.id);
      const names = filtered.map((item) => item.status_name);
      
      setDefaultTrolleyStatusIds(ids);
      setDefaultTrolleyStatusNames(names);
    }
  }, [trolleyStatusData?.trolley_list]);

  // Track if user has explicitly cleared filters
  const hasUserClearedFilters = useRef<boolean>(false);
  // Track if default filters have been applied on initial load
  const hasAppliedDefaultFilters = useRef<boolean>(false);
  // Track if we've loaded saved filters from storage (to prevent overwriting with defaults)
  const hasLoadedSavedFilters = useRef<boolean>(
    loadFiltersFromStorage(SUPERVISOR_TOTE_FILTERS_KEY) !== null ||
    loadFiltersFromStorage(SUPERVISOR_TROLLEY_FILTERS_KEY) !== null
  );
  
  useEffect(() => {
    // Only apply default filters on initial load if:
    // 1. We haven't applied defaults yet
    // 2. User hasn't explicitly cleared them
    // 3. No saved filters exist (hasLoadedSavedFilters is false)
    // 4. No current trolley filters are set
    if (
      !hasAppliedDefaultFilters.current &&
      !hasLoadedSavedFilters.current &&
      defaultTrolleyStatusIds.length &&
      !hasUserClearedFilters.current &&
      (!trolleyFilters || !trolleyFilters.status_ids?.length)
    ) {
      const defaultFilters = {
        status_ids: defaultTrolleyStatusIds,
        status: defaultTrolleyStatusNames,
      };
      setTrolleyFilters(defaultFilters);
      saveFiltersToStorage(SUPERVISOR_TROLLEY_FILTERS_KEY, defaultFilters);
      hasAppliedDefaultFilters.current = true;
    } else if (!hasAppliedDefaultFilters.current && defaultTrolleyStatusIds.length > 0) {
      // Mark as applied once we have default status IDs (even if we don't apply them)
      hasAppliedDefaultFilters.current = true;
    }
  }, [defaultTrolleyStatusIds, defaultTrolleyStatusNames, trolleyFilters]);

  const resolvedTotePriorityIds = useMemo(() => {
    if (!toteFilters?.priority) {
      return undefined;
    }

    const values = Array.isArray(toteFilters.priority)
      ? toteFilters.priority
      : [toteFilters.priority];

    const ids = values
      .filter((value): value is string => Boolean(value))
      .map((value) => priorityMap.get(value.toLowerCase()))
      .filter((value): value is number => typeof value === "number");

    return ids.length ? ids : undefined;
  }, [toteFilters?.priority, priorityMap]);

  const resolvedToteStatusIds = useMemo(() => {
    if (!toteFilters?.status) {
      return undefined;
    }

    const values = Array.isArray(toteFilters.status)
      ? toteFilters.status
      : [toteFilters.status];

    const ids = values
      .map((value) => statusMap.get(value.toLowerCase()))
      .filter((value): value is number => typeof value === "number");

    return ids.length ? ids : undefined;
  }, [toteFilters?.status, statusMap]);

  const priorityIdsParam =
    resolvedTotePriorityIds ??
    (toteFilters?.priority_ids && toteFilters.priority_ids.length
      ? toteFilters.priority_ids
      : undefined);

  const statusIdsParam =
    resolvedToteStatusIds ??
    (toteFilters?.status_ids && toteFilters.status_ids.length
      ? toteFilters.status_ids
      : undefined);

  const isToteTabActive = segment === SEGMENTS.TOTE;

  // Memoize tote query params - use full params when active, minimal params when inactive (for count)
  const toteQueryParams = useMemo(
    () => {
      const trimmedSearch = debouncedSearchQuery.trim();
      const baseParams = {
        page: 1,
        page_size: 100,
      };
      
      // When tote tab is active, include all filters and search
      if (isToteTabActive) {
        return {
          ...baseParams,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
          ...(toteFilters?.from_date ? { from_date: toteFilters.from_date } : {}),
          ...(toteFilters?.to_date ? { to_date: toteFilters.to_date } : {}),
          ...(toteFilters?.location_id
            ? { location_id: toteFilters.location_id }
            : {}),
          ...(statusIdsParam ? { status_ids: statusIdsParam } : {}),
          ...(priorityIdsParam ? { priority_ids: priorityIdsParam } : {}),
        };
      }
      
      // When tote tab is inactive, use minimal params (no filters) just to get count
      return baseParams;
    },
    [
      isToteTabActive,
      debouncedSearchQuery,
      toteFilters?.from_date,
      toteFilters?.to_date,
      toteFilters?.location_id,
      statusIdsParam,
      priorityIdsParam,
    ]
  );
  
  const {
    data: totes = [],
    isLoading: isTotesLoading,
    error: totesError,
    refetch: refetchTotes,
  } = useAllTotes(
    toteQueryParams,
    true // Always enable to get count even when tab is inactive
  );

  // Use default trolley status filters only if user hasn't explicitly cleared them
  const trolleyStatusIdsForQuery = useMemo(() => {
    if (trolleyFilters?.status_ids && trolleyFilters.status_ids.length) {
      return trolleyFilters.status_ids;
    } else if (!hasUserClearedFilters.current && defaultTrolleyStatusIds.length > 0) {
      // Only use default filters if user hasn't explicitly cleared them
      return defaultTrolleyStatusIds;
    }
    return undefined;
  }, [trolleyFilters?.status_ids, defaultTrolleyStatusIds]);

  const isTrolleyTabActive = segment === SEGMENTS.TROLLEY;

  // Memoize trolley query params - use full params when active, minimal params when inactive (for count)
  const trolleyQueryParams = useMemo(
    () => {
      const trimmedSearch = debouncedSearchQuery.trim();
      const baseParams = {
        page: 1,
        page_size: 100,
      };
      
      // When trolley tab is active, include all filters and search
      if (isTrolleyTabActive) {
        return {
          ...baseParams,
          ...(trimmedSearch ? { search: trimmedSearch } : {}),
          ...(trolleyFilters?.location_id
            ? { location_id: trolleyFilters.location_id }
            : {}),
          ...(trolleyFilters?.from_date
            ? { from_date: trolleyFilters.from_date }
            : {}),
          ...(trolleyFilters?.to_date ? { to_date: trolleyFilters.to_date } : {}),
          ...(trolleyFilters?.priority_flag !== undefined
            ? { priority_flag: trolleyFilters.priority_flag }
            : {}),
          ...(trolleyStatusIdsForQuery && trolleyStatusIdsForQuery.length > 0
            ? { status_ids: trolleyStatusIdsForQuery }
            : {}),
        };
      }
      
      // When trolley tab is inactive, use minimal params with default status filters for count
      // This ensures we get an accurate count that matches what will be shown when tab is activated
      // IMPORTANT: If defaultTrolleyStatusIds is empty, don't apply any status filter
      // This prevents filtering out all trolleys
      return {
        ...baseParams,
        ...(trolleyStatusIdsForQuery && trolleyStatusIdsForQuery.length > 0
          ? { status_ids: trolleyStatusIdsForQuery }
          : {}),
      };
    },
    [
      isTrolleyTabActive,
      debouncedSearchQuery,
      trolleyFilters?.location_id,
      trolleyFilters?.from_date,
      trolleyFilters?.to_date,
      trolleyFilters?.priority_flag,
      trolleyStatusIdsForQuery,
    ]
  );
  
  const {
    data: trolleys = [],
    isLoading: isTrolleysLoading,
    error: trolleysError,
    refetch: refetchTrolleys,
  } = useAllTrolleys(
    trolleyQueryParams,
    true // Always enable to get count even when tab is inactive
  );

  useEffect(() => {
    if (totesError) {
      notification.error({
        message: "Failed to load totes",
        description: "Unable to retrieve tote list. Please try again later.",
        duration: 4,
      });
    }
  }, [totesError]);

  useEffect(() => {
    if (trolleysError) {
      notification.error({
        message: "Failed to load trolleys",
        description: "Unable to retrieve trolley list. Please try again later.",
        duration: 4,
      });
    }
  }, [trolleysError]);

  // Refetch list API when tab changes
  useEffect(() => {
    if (segment === SEGMENTS.TOTE) {
      if (isToteTabActive) {
        refetchTotes();
      }
    } else {
      if (isTrolleyTabActive) {
        refetchTrolleys();
      }
    }
  }, [segment, refetchTotes, refetchTrolleys, isToteTabActive, isTrolleyTabActive]);

  // Always refetch lists when component mounts and is on landing page
  const isInitialMount = useRef<boolean>(true);
  
  useEffect(() => {
    const isOnLandingPage = location.pathname === CONSTANTS.PAGE_ROUTES.SUPERVISOR_LANDING;
    
    // Skip refetch only on very first app load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Still refetch on first mount if we're on landing page (handles direct navigation)
      if (isOnLandingPage) {
        // Call both APIs on initial load to get counts for both tabs
        refetchTotes();
        refetchTrolleys();
      }
      return;
    }
    
    // Always refetch lists whenever we're on the landing page
    if (isOnLandingPage) {
      console.log('Refetching lists on landing page');
      // Refetch both APIs to keep counts updated
      refetchTotes();
      refetchTrolleys();
    }
  }, [location.pathname, refetchTotes, refetchTrolleys]);

  const toteListingItems = useMemo<ListingItem[]>(() => {
    return totes.map((tote) => ({
      toteNo: tote.toteNo,
      status: tote.activityStatus,
      totalItems: parseInt(tote.itemCount) || 0,
      priority: tote.priority,
      registeredDate: tote.registeredDate,
      id: tote.id,
    }));
  }, [totes]);

  const sanitizedTrolleys = useMemo(() => {
    return trolleys.filter((trolley) => {
      const idLower = typeof trolley.id === "string"
        ? trolley.id.trim().toLowerCase()
        : "";
      const statusLower = (trolley.status || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

      const looksLikeToteId = idLower.startsWith("tote");
      const isToteStatus =
        statusLower.length > 0 && TOTE_STATUS_KEYWORDS.has(statusLower);

      // Only filter based on status and naming, not ID matching
      // ID matching was causing valid trolleys to be filtered out
      return !looksLikeToteId && !isToteStatus;
    });
  }, [trolleys]);

  const trolleyListingItems = useMemo<ListingItem[]>(() => {
    return sanitizedTrolleys.map((trolley) => ({
      toteNo: trolley.id,
      status: trolley.status,
      totalItems: trolley.availableTotes ?? 0,
      priority: trolley.priority,
      registeredDate: trolley.registeredDate,
      id: trolley.id,
      fullData: trolley,
    }));
  }, [sanitizedTrolleys]);

  const displayedItems =
    segment === SEGMENTS.TOTE ? toteListingItems : trolleyListingItems;
  const isCurrentLoading =
    segment === SEGMENTS.TOTE ? isTotesLoading : isTrolleysLoading;

  const isFilterApplied = hasActiveFilters(
    segment === SEGMENTS.TOTE ? toteFilters : trolleyFilters
  );

  const handleFilterApply = useCallback(
    (appliedFilters: FilterValues) => {
      if (segment === SEGMENTS.TOTE) {
        // If filters are cleared, set to null
        if (!hasActiveFilters(appliedFilters)) {
          setToteFilters(null);
          saveFiltersToStorage(SUPERVISOR_TOTE_FILTERS_KEY, null);
        } else {
          setToteFilters(appliedFilters);
          saveFiltersToStorage(SUPERVISOR_TOTE_FILTERS_KEY, appliedFilters);
        }
      } else {
        // If filters are cleared, set to null and mark that user has cleared them
        if (!hasActiveFilters(appliedFilters)) {
          hasUserClearedFilters.current = true;
          setTrolleyFilters(null);
          saveFiltersToStorage(SUPERVISOR_TROLLEY_FILTERS_KEY, null);
        } else {
          // User has applied filters, allow clearing in future
          hasUserClearedFilters.current = false;
          setTrolleyFilters(appliedFilters);
          saveFiltersToStorage(SUPERVISOR_TROLLEY_FILTERS_KEY, appliedFilters);
        }
      }
      // Modal closing is handled by FilterModal's handleApply, not here
    },
    [segment]
  );

  const handleCardClick = (item: ListingItem) => {
    if (segment === SEGMENTS.TOTE) {
      // Use id (record ID) instead of toteNo for API compatibility
      const toteId = item.id || String(item.toteNo);
      navigate(
        `/supervisor/tote-info/${encodeURIComponent(String(toteId))}`
      );
    } else {
      const trolleyId = item.id || String(item.toteNo);
      navigate(
        `/supervisor/trolley-info/${encodeURIComponent(String(trolleyId))}`
      );
    }
  };

  const handleToteScanned = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      return false;
    }

    setIsCheckingTote(true);
    try {
      const response = await api.checkTote({ tote_id: trimmed });
      // Check if tote is registered (has tote_details in response)
      if (
        response.status_code === 200 &&
        (response.tote_details?.tote_id || response.tote_details?.id)
      ) {
        // Extract the record ID from checkTote response
        const toteRecordId = response.tote_details?.id;
        // Use record ID for navigation, fallback to scanned code if record ID not available
        const toteIdForNavigation = toteRecordId ? String(toteRecordId) : trimmed;

        // Check tote status
        const toteStatus = response.tote_details?.tote_status?.toLowerCase() || "";

        // For Supervisor role, only navigate to info page if status is NOT "Completed"
        // Completed totes should show error message
        if (toteStatus === "completed") {
          setScanErrorMessage(
            `Tote (ID: ${trimmed}) has completed its journey. Please contact the Operator for re-registration.`
          );
          setIsCheckingTote(false);
          return false;
        }

        // For other statuses, navigate to tote info page
        // Use record ID from checkTote response
        setIsCheckingTote(false);
        setScanErrorMessage(null);
        navigate(`/supervisor/tote-info/${encodeURIComponent(toteIdForNavigation)}`);
        return true;
      }

      notification.info({
        message: "Tote Not Found",
        description: `Tote ID: ${trimmed} is not registered in our system`,
      });
      setIsCheckingTote(false);
      setTimeout(() => {
        navigate(CONSTANTS.PAGE_ROUTES.SUPERVISOR_LANDING);
      }, 1000);
      return false;
    } catch (err) {
      console.error("Tote scan failed", err);
      notification.info({
        message: "Tote Not Found",
        description: `Tote ID: ${trimmed} is not registered in our system`,
      });
      setIsCheckingTote(false);
      setTimeout(() => {
        navigate(CONSTANTS.PAGE_ROUTES.SUPERVISOR_LANDING);
      }, 1000);
      return false;
    }
  };

  const handleTrolleyScanned = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      return false;
    }

    setIsCheckingTrolley(true);
    try {
      const response = await api.checkTrolley({ trolley_id: trimmed });

      const hasTrolleyDetails = Boolean(
        response?.trolley_details?.trolley_id || response?.trolley_details?.id
      );

      if (
        (response.message && response.message.includes("isn't registered")) ||
        !hasTrolleyDetails
      ) {
        notification.info({
          message: "Trolley Not Found",
          description:
            response?.message ||
            `Trolley ID: ${trimmed} is not registered in our system`,
        });
        setIsCheckingTrolley(false);
        setTimeout(() => {
          navigate(CONSTANTS.PAGE_ROUTES.SUPERVISOR_LANDING);
        }, 1000);
        return false;
      }

      // Extract the trolley_id string from checkTrolley response (not the numeric record ID)
      const trolleyIdString = response?.trolley_details?.trolley_id || trimmed;

      setScanErrorMessage(null);
      setIsCheckingTrolley(false);
      // Use trolley_id string for navigation (e.g., "TR006", "asd")
      navigate(`/supervisor/trolley-info/${encodeURIComponent(trolleyIdString)}`);
      return true;
    } catch (err) {
      console.error("Trolley scan failed", err);
      notification.info({
        message: "Trolley Not Found",
        description: `Trolley ID: ${trimmed} is not registered in our system`,
      });
      setIsCheckingTrolley(false);
      setTimeout(() => {
        navigate(CONSTANTS.PAGE_ROUTES.SUPERVISOR_LANDING);
      }, 1000);
      return false;
    }
  };

  const handleScanErrorClear = () => {
    setScanErrorMessage(null);
  };

  const searchPlaceholder =
    segment === SEGMENTS.TOTE ? "Search Tote" : "Search Trolley";
  // const dividerLabel =
  //   segment === SEGMENTS.TOTE ? "Registered Totes" : "Registered Trolleys";
  const filterType: "tote" | "trolley" =
    segment === SEGMENTS.TOTE ? "tote" : "trolley";
  // Pass empty object when filters are explicitly cleared (null) to prevent using defaults
  // Pass undefined only when filters have never been set (should use defaults on initial load)
  // For trolley: if filters are null and user has cleared them, pass {}; otherwise undefined for defaults
  // For tote: if filters are null, pass {}; otherwise undefined
  const initialFilterValues =
    segment === SEGMENTS.TOTE
      ? toteFilters !== null && toteFilters !== undefined
        ? toteFilters
        : toteFilters === null
        ? {}
        : undefined
      : trolleyFilters !== null && trolleyFilters !== undefined
      ? trolleyFilters
      : trolleyFilters === null && hasUserClearedFilters.current
      ? {}
      : undefined;
  const defaultFilterValues =
    segment === SEGMENTS.TOTE
      ? undefined
      : defaultTrolleyStatusIds.length
      ? {
          status_ids: defaultTrolleyStatusIds,
          status: defaultTrolleyStatusNames,
        }
      : undefined;

  return (
    <div className={Styles.mainContainer}>
      <Header />
      <div className={Styles.listingContainer}>
        <Row align="middle" justify="space-between">
          <Col span={20}>
            <Input
              prefix={<Icon component={SearchIcon} />}
              placeholder={searchPlaceholder}
              style={{ width: "100%" }}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              allowClear
              enterKeyHint="search"
              onPressEnter={(e) => {
                // Trigger search when search button is pressed
                setDebouncedSearchQuery(searchQuery);
                // Close the keyboard
                e.currentTarget.blur();
              }}
            />
          </Col>
          <Col span={3}>
            <Badge dot={isFilterApplied} offset={[-2, 2]}>
              <Button
                icon={<Icon component={FilterIcon} />}
                onClick={() => setIsFilterOpen(true)}
              />
            </Badge>
          </Col>
        </Row>
        <Row justify="center" className={Styles.spacingRow}>
          <Segmented
            options={[
              {
                label: (
                  <div>
                    {SEGMENTS.TOTE} ({totes.length})
                  </div>
                ),
                value: SEGMENTS.TOTE,
              },
              {
                label: (
                  <div>
                    {SEGMENTS.TROLLEY} ({sanitizedTrolleys.length})
                  </div>
                ),
                value: SEGMENTS.TROLLEY,
              },
            ]}
            className="mainSegments"
            value={segment}
            onChange={(value) => {
              setSegment(value as SegmentType);
              setSearchQuery("");
              setDebouncedSearchQuery("");
            }}
          />
        </Row>

        {/* <Divider style={{ fontSize: "13px" }} orientation="left">
          {dividerLabel}
        </Divider> */}

        {isCurrentLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin indicator={<CustomSpin />} />
          </div>
        ) : displayedItems.length > 0 ? (
          <div
            className={Styles.spacingRow}
            style={{
              overflowY: "auto",
              height: segment === SEGMENTS.TROLLEY ? "60vh" : "67vh",
              // Account for safe area bottom inset
              paddingBottom: `calc(80px + env(safe-area-inset-bottom, 0px))`,
              WebkitOverflowScrolling: "touch",
            }}
          >
            {displayedItems.map((item, index) => (
              <ListingCards
                key={`${item.id || item.toteNo}-${segment}-${index}`}
                listingType={
                  segment === SEGMENTS.TOTE ? "supervisorTotes" : SEGMENTS.TROLLEY
                }
                details={item}
                onClick={() => handleCardClick(item)}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
              color: "#999",
              fontSize: "14px",
            }}
          >
            No more items to load
          </div>
        )}
      </div>

      <UniversalScanButton
        availableActions={["tote", "trolley"]}
        onToteScanned={handleToteScanned}
        onTrolleyScanned={handleTrolleyScanned}
        isCheckingTote={isCheckingTote}
        isCheckingTrolley={isCheckingTrolley}
        scanErrorMessage={scanErrorMessage}
        onScanErrorClear={handleScanErrorClear}
        keepScannerOpenOnSuccess={false}
      />

      <FilterModal
        key={segment}
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
        filterType={filterType}
        initialValues={initialFilterValues}
        defaultValues={defaultFilterValues}
      />
    </div>
  );
};

export default SupervisorMobileLanding;
